import { useEffect } from 'react';
import { getResourceUsage, measurePerformance } from '@/lib/performance';

interface PerformanceMonitorProps {
  enabled?: boolean;
}

export default function PerformanceMonitor({ enabled = false }: PerformanceMonitorProps) {
  useEffect(() => {
    if (!enabled || process.env.NODE_ENV !== 'development') return;

    const logPerformanceMetrics = () => {
      // Core Web Vitals monitoring
      if ('PerformanceObserver' in window) {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (entry.processingStart && entry.startTime) {
              console.log('FID:', entry.processingStart - entry.startTime);
            }
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift (CLS)
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (entry.hadRecentInput !== undefined && !entry.hadRecentInput && entry.value) {
              console.log('CLS:', entry.value);
            }
          });
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Resource loading times
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.duration > 1000) { // Log slow resources (>1s)
              console.warn('Slow resource:', entry.name, `${entry.duration}ms`);
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });

        // Cleanup observers on unmount
        return () => {
          lcpObserver.disconnect();
          fidObserver.disconnect();
          clsObserver.disconnect();
          resourceObserver.disconnect();
        };
      }
    };

    const monitorMemoryUsage = () => {
      const usage = getResourceUsage();
      if (usage && parseFloat(usage.usage) > 80) {
        console.warn('High memory usage detected:', usage);
      }
    };

    // Initial metrics
    logPerformanceMetrics();
    
    // Monitor memory usage every 30 seconds
    const memoryInterval = setInterval(monitorMemoryUsage, 30000);
    
    // Log bundle size information
    if (window.performance && window.performance.getEntriesByType) {
      const navigationEntries = window.performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0) {
        const nav = navigationEntries[0] as any; // Use any to avoid TypeScript issues
        console.log('Page Load Metrics:', {
          'DNS Lookup': (nav.domainLookupEnd || 0) - (nav.domainLookupStart || 0),
          'TCP Connection': (nav.connectEnd || 0) - (nav.connectStart || 0),
          'Request': (nav.responseStart || 0) - (nav.requestStart || 0),
          'Response': (nav.responseEnd || 0) - (nav.responseStart || 0),
          'DOM Processing': (nav.domComplete || 0) - (nav.responseEnd || 0),
          'Total Load Time': (nav.loadEventEnd || 0) - (nav.fetchStart || 0)
        });
      }
    }

    return () => {
      clearInterval(memoryInterval);
    };
  }, [enabled]);

  // Monitor React component rendering performance
  useEffect(() => {
    if (!enabled || process.env.NODE_ENV !== 'development') return;

    // Track expensive re-renders
    const originalConsoleWarn = console.warn;
    console.warn = (...args) => {
      if (args[0] && args[0].includes('Warning: ')) {
        console.warn('React Performance Warning:', ...args);
      }
      originalConsoleWarn.apply(console, args);
    };

    return () => {
      console.warn = originalConsoleWarn;
    };
  }, [enabled]);

  return null; // This component doesn't render anything
}